"use client"

import { clsx } from "clsx"
import type { NewsletterModel } from "@/app/[domain]/(sites)/_actions/newsletter/newsletter.types"
import { NewsletterSignUp } from "@/components/Newsletter/NewsletterSignUp/NewsletterSignUp.component"
import { useNewsletterSignup } from "@/components/Newsletter/NewsletterSignUp/use-newsletter-signup.hook"
import { NewsletterSignUpFailedBox } from "@/components/Newsletter/NewsletterSignUpFailedBox/NewsletterSignUpFailedBox.component"
import { NewsletterSignUpForm } from "@/components/Newsletter/NewsletterSignUpForm/NewsletterSignUpForm.component"
import { NewsletterSignUpImage } from "@/components/Newsletter/NewsletterSignUpImage/NewsletterSignUpImage.component"
import {
  NewsletterSignUpSuccessBox,
  newsletterSuccessMessage,
} from "@/components/Newsletter/NewsletterSignUpSuccessBox/NewsletterSignUpSuccessBox.component"
import { SwitchFetchable } from "@/components/SwitchFetchable/SwitchFetchable.component"
import { Translation } from "@/components/Translation/Translation.component"
import type { Fetchable } from "@/services/utilities/fetchable"
import { initial, isPending } from "@/services/utilities/fetchable"
import type { ConversionEvent } from "@/vendors/Kilkaya/kilkaya.types"

type WatchNewsletterSignUpProps = {
  readonly className?: string
  readonly conversionEventTags?: ConversionEvent
  readonly initialSubmissionState?: Fetchable
  readonly model: NewsletterModel
}

export function WatchNewsletterSignUp({
  className,
  conversionEventTags,
  initialSubmissionState = initial(),
  model,
}: WatchNewsletterSignUpProps) {
  const {
    reset,
    submissionState: submission,
    submit,
    permissionText,
  } = useNewsletterSignup(initialSubmissionState)

  if (!permissionText) {
    return null
  }

  return (
    <NewsletterSignUp className={clsx("bg-whisper text-charcoal", className)}>
      <div className="col-span-full">
        <SwitchFetchable
          fetchable={submission}
          renderDefault={() => (
            <NewsletterSignUpForm
              isPending={isPending(submission)}
              model={model}
              onSubmit={submit}
              permissionText={permissionText}
              renderImage={() => (
                <NewsletterSignUpImage className="bg-primary brightness-90" />
              )}
            />
          )}
          renderFailed={() => (
            <NewsletterSignUpFailedBox className="px-2 py-3" reset={reset} />
          )}
          renderSucceeded={() => (
            <NewsletterSignUpSuccessBox
              className="px-2 py-3"
              conversionEvent={conversionEventTags}
              hasHomeButton={false}
            >
              <h1 className="mb-1 font-theme-bold text-2xl tracking-tight lg:text-3xl">
                <Translation model={newsletterSuccessMessage} />
              </h1>
            </NewsletterSignUpSuccessBox>
          )}
        />
      </div>
    </NewsletterSignUp>
  )
}
